import * as React from 'react';
import './TipTapEditor.css';
import {
  EditorProvider,
  ReactNodeViewRenderer,
  useCurrentEditor,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import FixedMenu from './FixedMenu';
import { styled } from '@mui/material/styles';
import { Box, Tab, Tabs } from '@mui/material';
import Image from '@tiptap/extension-image';
import CustomImageWithOptions from './CustomImageWithOptions';
import { Eye } from '@phosphor-icons/react';
import { PencilSimple } from '@phosphor-icons/react';
import TabPanel from '../TabPanel';

export type CustomImageAttributes = {
  mediaId: string;
  src: string;
  caption: string;
};

const CustomImage = Image.extend({
  draggable: true,
  addAttributes() {
    return {
      ...this.parent?.(),
      mediaId: {
        default: '',
      },
      caption: {
        default: '',
      },
    };
  },
  // addCommands() {
  //   return {
  //     ...this.parent?.(),
  //     setMediaId:
  //       (id: string) =>
  //       ({ commands }) => {
  //         return commands.updateAttributes('image', { mediaId: id });
  //       },
  //   };
  // },
  addNodeView() {
    return ReactNodeViewRenderer(CustomImageWithOptions);
  },
});

// define your extension array
const extensions = [
  StarterKit.configure({
    heading: {
      levels: [2, 3],
    },
  }),
  Link.configure({
    openOnClick: false,
    autolink: true,
  }),
  CustomImage,
];

type Props = {
  onUpdate: (content: string) => void;
  content: string;
  children?: React.ReactNode;
};

const MenuContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 0),
}));

export default function TiptapEditor({ content, children, onUpdate }: Props) {
  const [tab, setTab] = React.useState<'editor' | 'preview'>('editor');

  return (
    <EditorProvider
      extensions={extensions}
      content={content}
      onUpdate={({ editor }) => {
        if (editor) {
          onUpdate(editor.getHTML());
        }
      }}
      slotBefore={
        <>
          <Tabs value={tab} onChange={(_, value) => setTab(value)}>
            <Tab
              value="editor"
              label="Edit"
              icon={<PencilSimple size={24} />}
              iconPosition="start"
              sx={{ minHeight: 52 }}
            />
            <Tab
              value="preview"
              label="Preview"
              icon={<Eye size={24} />}
              iconPosition="start"
              sx={{ minHeight: 52 }}
            />
          </Tabs>
          <TabPanel hidden={tab !== 'editor'}>
            {children}
            <MenuContainer>
              <FixedMenu />
            </MenuContainer>
          </TabPanel>
          <TabPanel hidden={tab !== 'preview'}>
            <EditorPreview />
          </TabPanel>
        </>
      }
      editorProps={{
        attributes: {
          class: tab === 'preview' ? 'editor-hidden' : '',
          style: `font-family: ${
            document.body.style.fontFamily || 'Roboto, sans-serif'
          }; font-size: 1rem; color: inherit;`,
        },
        transformPastedHTML(html) {
          return html.replace(/<img.*?>/g, '');
        },
      }}
    >
      {children}
    </EditorProvider>
  );
}

function EditorPreview() {
  const { editor } = useCurrentEditor();

  if (!editor) return <p>No editor</p>;

  const html = editor.getHTML();

  return (
    <div
      className="editor-preview"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
